import { useState } from 'react';
import { Alert, Button, Divider, Group, Paper, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { dryKg, yieldDryPct, yieldVariancePct } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { MOVEMENT_TYPE_DESCRIPTIONS, MOVEMENT_TYPE_LABELS } from '../../../lib/enumLabels';
import { useAllProducts, useCloseRun, useLocations, usePostRun } from '../api';
import { formatMoneyCents, formatQty } from '../format';
import type { ProductDto, RecipeDto, RunDetailDto } from '../types';
import { YieldVarianceBadge } from './YieldVarianceBadge';

/**
 * Yield/mass-balance dry-kg basis, scoped to baseUom==='KG' lines only — mirrors
 * services/api/src/modules/production/runs.ts's `/post` handler EXACTLY (R2/R6:
 * inputs/outputs are joined with `products.base_uom`, and the dry-mass sums that
 * feed yield are scoped to KG lines on BOTH sides). An EACH line's `qtyKg` column
 * holds a unit count, not a mass — folding it in here (as the old unscoped
 * `sumDryKg` did) fabricated a live yield/variance number for count-based runs
 * that the server would then post as NULL, contradicting itself. Same `dryKg`
 * from `@zebra/core` the server calls, just filtered first.
 */
function sumKgDryKg(lines: { productId: string; qtyKg: string; mcPct: string | null }[], productsById: Map<string, ProductDto>): number {
  return lines.reduce((sum, l) => {
    if (productsById.get(l.productId)?.baseUom !== 'KG') return sum;
    return sum + Number(dryKg(l.qtyKg, l.mcPct));
  }, 0);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700}>{value}</Text>
    </div>
  );
}

const NO_YIELD_BASIS_CAPTION = 'Yield does not apply to count-based outputs';

/** Yield-vs-recipe live preview + ledger-effect preview + Close/Post actions
 * (BUILD_SPEC.md §8.3, §11: "every posting screen shows a plain-language preview
 * of ledger effects before Post"). Reused by both the "Record a shift" wizard's
 * final step and the run detail page (kiln batches close the same way, just days
 * later) — one implementation, one place the math can drift from the server's
 * (uses the SAME `@zebra/core` `dryKg`/`yieldDryPct`/`yieldVariancePct` the server
 * calls at `/post`, per the established `ledgerPreview.ts` precedent). */
export function ClosePostPanel({
  run,
  recipe,
  missingConsumableCount,
}: {
  run: RunDetailDto;
  recipe: RecipeDto | undefined;
  missingConsumableCount: number;
}) {
  const canOperate = useCanOperate();
  const locationsQuery = useLocations(run.siteId);
  // ALL products (active + inactive), matching the server's un-filtered products
  // join: a line for a product deactivated after the run opened must still count
  // toward the KG basis, or this preview diverges from what `/post` stores.
  const productsQuery = useAllProducts();
  const closeRun = useCloseRun(run.id);
  const postRun = usePostRun(run.id);
  const [consumablesFromLocationId, setConsumablesFromLocationId] = useState<string | null>(null);

  const productsById = new Map((productsQuery.data?.items ?? []).map((p) => [p.id, p]));

  // Quantized to 3dp before the zero check — same as the server's Decimal
  // `.toDecimalPlaces(3)` — so this preview's "is there a KG basis at all?"
  // decision can never disagree with the server's on a sub-milligram edge.
  const inputsDryBasis = Number(sumKgDryKg(run.inputs, productsById).toFixed(3));
  const outputsDryBasis = Number(sumKgDryKg(run.outputs, productsById).toFixed(3));
  const hasLines = run.inputs.length > 0 && run.outputs.length > 0;
  const hasKgBasis = inputsDryBasis > 0 && outputsDryBasis > 0;
  const yieldActual = hasLines && hasKgBasis ? yieldDryPct(inputsDryBasis.toFixed(3), outputsDryBasis.toFixed(3)) : null;
  const yieldVariance = yieldActual !== null && recipe ? yieldVariancePct(yieldActual, Number(recipe.expectedYieldDryPct)) : null;
  // Distinct from "not computed yet — the run has no lines": this is the R2 case,
  // lines exist but their KG dry-mass basis is zero (a pure-EACH run, or a
  // KG->EACH packing run) — there is nothing to report a yield % against, same as
  // the server posting NULL rather than a fabricated 0%. Gated on the product
  // lookup having loaded so a still-fetching lookup (empty map -> zero basis)
  // can't flash the caption at a KG run.
  const noYieldBasis = Boolean(productsQuery.data) && hasLines && !hasKgBasis;

  // True exactly when the server's own `/close` guard will refuse to
  // auto-backflush (services/api/src/modules/production/runs.ts ~436-472):
  // recipe consumables are still pending, and this run's KG-denominated basis
  // is zero — a pure-EACH/packing run, or a KG->EACH conversion — so there is
  // nothing to compute `qtyPerOutputTonne x KG output tonnes` against. Gated on
  // the product lookup having loaded, same reasoning as `noYieldBasis` above
  // (an empty lookup map makes every line's basis look like zero, which would
  // falsely block Close on an ordinary KG run while products are still
  // fetching).
  const backflushImpossible = Boolean(productsQuery.data) && missingConsumableCount > 0 && !hasKgBasis;

  const canClose =
    run.status === 'OPEN' &&
    run.inputs.length > 0 &&
    run.outputs.length > 0 &&
    !backflushImpossible &&
    (missingConsumableCount === 0 || Boolean(consumablesFromLocationId));
  const canPost = run.status === 'CLOSED';

  function handleClose() {
    closeRun.mutate({ consumablesFromLocationId: missingConsumableCount > 0 ? consumablesFromLocationId : null });
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="sm">
        <Text fw={600}>Yield vs recipe</Text>
        <Group grow>
          <Stat label="Inputs (dry)" value={`${formatQty(inputsDryBasis)} kg`} />
          <Stat label="Outputs (dry)" value={`${formatQty(outputsDryBasis)} kg`} />
          <Stat label="Actual yield" value={yieldActual !== null ? `${yieldActual.toFixed(1)}%` : '—'} />
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              vs recipe ({recipe ? `${recipe.expectedYieldDryPct}%` : '—'})
            </Text>
            {yieldVariance !== null ? <YieldVarianceBadge variancePct={yieldVariance} /> : <Text fw={700}>—</Text>}
          </div>
        </Group>
        {noYieldBasis && (
          <Text size="xs" c="dimmed" fs="italic">
            {NO_YIELD_BASIS_CAPTION} — this run's KG dry-mass basis is zero (its inputs and/or outputs are count-based/EACH
            products).
          </Text>
        )}

        <Divider />
        <Text fw={600}>Ledger effect preview</Text>
        <Table.ScrollContainer minWidth={640}>
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Tooltip label={MOVEMENT_TYPE_DESCRIPTIONS.PRODUCTION_OUT}>
                    <span>Inputs taken out of stock ({MOVEMENT_TYPE_LABELS.PRODUCTION_OUT})</span>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{run.inputs.length} line(s)</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Tooltip label={MOVEMENT_TYPE_DESCRIPTIONS.ISSUE}>
                    <span>Consumables issued ({MOVEMENT_TYPE_LABELS.ISSUE})</span>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  {run.consumables.length} recorded
                  {missingConsumableCount > 0 && !backflushImpossible ? ` + ${missingConsumableCount} backflushed at close` : ''}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Tooltip label={MOVEMENT_TYPE_DESCRIPTIONS.PRODUCTION_IN}>
                    <span>Outputs added to stock ({MOVEMENT_TYPE_LABELS.PRODUCTION_IN})</span>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  {run.outputs.length} line(s), cost split by{' '}
                  {recipe?.outputs.some((o) => o.splitPct != null) ? 'recipe split %' : 'dry-mass share'}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {run.status === 'OPEN' && missingConsumableCount > 0 && !backflushImpossible && (
          <Select
            label="Backflush remaining recipe consumables from"
            placeholder="Choose a location"
            data={(locationsQuery.data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
            value={consumablesFromLocationId}
            onChange={setConsumablesFromLocationId}
            searchable
          />
        )}
        {run.status === 'OPEN' && backflushImpossible && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Can't close this run yet">
            {missingConsumableCount} recipe consumable{missingConsumableCount === 1 ? '' : 's'} can&apos;t be
            auto-backflushed on a zero-KG run — record {missingConsumableCount === 1 ? 'it' : 'them'} as actual
            quantities before closing.
          </Alert>
        )}

        {closeRun.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {closeRun.error instanceof ApiRequestError ? closeRun.error.message : 'Failed to close run'}
          </Alert>
        )}
        {postRun.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {postRun.error instanceof ApiRequestError ? postRun.error.message : 'Failed to post run'}
          </Alert>
        )}
        {postRun.isSuccess && postRun.data && (
          <Alert color="green" icon={<IconCircleCheck size={16} />} title="Posted">
            {postRun.data.summary} Cost: {formatMoneyCents(postRun.data.runCostCents)}.
          </Alert>
        )}

        <Group justify="flex-end">
          {run.status === 'OPEN' && (
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button disabled={!canOperate || !canClose} loading={closeRun.isPending} onClick={handleClose}>
                  Close run
                </Button>
              </span>
            </Tooltip>
          )}
          {run.status === 'CLOSED' && (
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button disabled={!canOperate || !canPost} loading={postRun.isPending} onClick={() => postRun.mutate()}>
                  Post
                </Button>
              </span>
            </Tooltip>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
